<template>
  <div class="asset-selector">
    <div class="flex items-center mt-3">
      <select
        class="px-2 py-1 border border-gray-300 custom-focus rounded-md w-20 mr-2 text-sm"
        :value="newDep.type"
        @change="updateNewDep({ ...newDep, type: $event.target.value })"
      >
        <option value="script">script</option>
        <option value="style">style</option>
        <option value="image">image</option>
        <option value="video">video</option>
      </select>

      <input
        :value="newDep.url"
        @input="updateNewDep({ ...newDep, url: $event.target.value, is_global: false, asset_id: null, asset_version: null, asset_name: null, asset_original_name: null, asset_version_note: null })"
        class="flex-1 px-3 py-2 border border-gray-300 rounded-md custom-focus h-8"
        type="text"
        placeholder="url"
        :disabled="newDep.url.startsWith('file:')"
      />

      <span
        v-if="newDep.url.startsWith('file:')"
        @click="updateNewDep({ ...newDep, url: '', is_global: false, asset_id: null, asset_version: null, asset_name: null, asset_original_name: null, asset_version_note: null })"
        class="mx-2 cursor-pointer opacity-30 hover:opacity-95"
      >
        <font-awesome-icon icon="times" />
      </span>

      <span class="mx-4 text-gray-600 text-sm">or</span>

      <!-- Asset Selector Button -->
      <button
        @click="showAssetModal = true"
        class="cursor-pointer text-sm px-4 py-2 text-white rounded-md h-8 flex items-center justify-center transition-all custom-file-input text-center mr-2"
      >
        <i class="fa-solid fa-folder mr-2"></i>
        Choose from Assets
      </button>

      <!-- Custom file input -->
      <label
        for="file-upload"
        class="cursor-pointer text-sm px-4 py-2 text-white rounded-md h-8 flex items-center justify-center transition-all custom-file-input text-center"
        :class="{ 'opacity-70': newDep.loading }"
      >
        <span v-if="newDep.loading">
          <i class="fa-solid fa-spinner fa-spin mr-2"></i>
          Uploading...
        </span>
        <span v-else class="lg:text-base md:text-sm">Upload New</span>
      </label>
      <input
        :key="newDep.url"
        type="file"
        id="file-upload"
        class="hidden"
        @change="handleUploadFile"
        :disabled="newDep.loading"
      />

      <span
        class="ml-2 cursor-pointer opacity-30 hover:opacity-95"
        @click="addDepFile"
      >
        <font-awesome-icon icon="plus" />
      </span>
    </div>

    <!-- Asset Selection Modal -->
    <vue-final-modal
      v-model="showAssetModal"
      :click-to-close="false"
      :content-style="{
        width: '80vw',
        height: '80vh',
        maxWidth: '1200px',
        margin: '10vh auto',
      }"
    >
      <div class="bg-white w-full h-full flex flex-col">
        <div class="flex items-center justify-between p-4 border-b">
          <h3 class="text-lg font-semibold">Select Asset</h3>
          <button
            @click="showAssetModal = false"
            class="text-gray-500 hover:text-gray-700"
          >
            <font-awesome-icon icon="times" />
          </button>
        </div>
        
        <div class="flex-1 overflow-hidden flex flex-col">
          <!-- Search and Filter -->
          <div class="p-4 border-b">
            <div class="flex gap-4">
              <input
                v-model="searchText"
                type="text"
                placeholder="Search assets..."
                class="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              />
              <select v-model="filterType" class="px-3 py-2 border border-gray-300 rounded-md">
                <option value="">All Types</option>
                <option value="script">Script Files</option>
                <option value="style">Style Files</option>
                <option value="image">Images</option>
                <option value="video">Videos</option>
              </select>
            </div>
          </div>

          <!-- Asset List -->
          <div class="flex-1 overflow-auto p-4">
            <div v-if="loading" class="flex items-center justify-center h-32">
              <i class="fa-solid fa-spinner fa-spin text-2xl"></i>
            </div>
            <div v-else-if="filteredAssets.length === 0" class="text-center text-gray-500 py-8">
              No assets found
            </div>
            <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div
                v-for="asset in filteredAssets"
                :key="asset._id"
                @click="selectAsset(asset)"
                class="border border-gray-200 rounded-lg p-3 cursor-pointer hover:border-blue-500 hover:shadow-md transition-all"
                :class="{ 'border-blue-500 bg-blue-50': selectedAsset?._id === asset._id }"
              >
                <div class="flex items-start space-x-3">
                  <div class="flex-shrink-0">
                    <i :class="getFileIcon(asset.file_type, asset.original_name)" class="text-2xl text-gray-600"></i>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h4 class="text-sm font-medium text-gray-900 truncate">
                      {{ asset.name }}
                    </h4>
                    <p class="text-xs text-gray-500 truncate">
                      {{ asset.original_name }}
                    </p>
                    <div class="flex items-center space-x-2 mt-2">
                      <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium" :class="getTypeBadgeClass(asset.file_type, asset.original_name)">
                        {{ getFileTypeLabel(asset.file_type, asset.original_name) }}
                      </span>
                      <span class="text-xs text-gray-500">
                        {{ formatFileSize(asset.file_size) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Version Selector -->
          <div v-if="selectedAsset" class="border-t p-4">
            <h4 class="text-base font-semibold mb-3">
              Select Version for {{ selectedAsset.name }}
            </h4>
            <div v-if="versionsLoading" class="flex items-center gap-2 text-gray-500">
              <i class="fa-solid fa-spinner fa-spin"></i>
              Loading versions...
            </div>
            <div v-else-if="assetVersions.length === 0" class="text-gray-500 text-sm">
              No versions available for this asset.
            </div>
            <div v-else class="flex flex-col gap-2">
              <select v-model="selectedVersionId" class="px-3 py-2 border border-gray-300 rounded-md w-full md:w-1/2">
                <option
                  v-for="version in assetVersions"
                  :key="version._id"
                  :value="version._id"
                >
                  v{{ version.version || 1 }}
                  <template v-if="selectedAsset?.version_meta?.published_version === version.version">
                    • Default
                  </template>
                  • {{ formatDate(version.createdAt) }} • {{ formatFileSize(version.file_size) }}
                </option>
              </select>
              <div class="text-xs text-gray-500">
                <div>Filename: {{ selectedVersion?.original_name || 'N/A' }}</div>
                <div v-if="selectedVersion?.version_note">Notes: {{ selectedVersion.version_note }}</div>
              </div>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="p-4 border-t flex justify-end space-x-2">
            <button
              @click="showAssetModal = false"
              class="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              @click="confirmSelection"
              :disabled="!selectedAsset || !selectedVersion"
              class="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Select Asset
            </button>
          </div>
        </div>
      </div>
    </vue-final-modal>
  </div>
</template>

<script>
import { uploadFile } from "../service/site";
import * as assetService from "../service/asset";

export default {
  name: "AssetSelector",
  props: {
    newDep: {
      type: Object,
      required: true
    }
  },
  emits: ["update:newDep", "addDepFile"],
  data() {
    return {
      showAssetModal: false,
      assets: [],
      loading: false,
      searchText: "",
      filterType: "",
      selectedAsset: null,
      assetVersions: [],
      versionsLoading: false,
      selectedVersionId: null
    };
  },
  computed: {
    filteredAssets() {
      let filtered = this.assets;
      
      // Filter by search text
      if (this.searchText.trim()) {
        const search = this.searchText.trim().toLowerCase();
        filtered = filtered.filter(asset => 
          asset.name.toLowerCase().includes(search) || 
          asset.original_name.toLowerCase().includes(search)
        );
      }
      
      // Filter by type
      if (this.filterType) {
        filtered = filtered.filter(asset => {
          const fileType = asset.file_type;
          if (this.filterType === 'script') {
            return fileType === 'application/javascript' || 
                   fileType === 'text/javascript' || 
                   fileType === 'application/x-javascript' ||
                   (fileType.startsWith('text/') && asset.original_name.match(/\.(js|jsx|ts|tsx)$/i));
          } else if (this.filterType === 'style') {
            return fileType === 'text/css' || 
                   (fileType.startsWith('text/') && asset.original_name.match(/\.(css|scss|sass|less)$/i));
          } else if (this.filterType === 'image') {
            return fileType.startsWith('image/');
          } else if (this.filterType === 'video') {
            return fileType.startsWith('video/');
          }
          return true;
        });
      }
      
      return filtered;
    },
    selectedVersion() {
      return this.assetVersions.find(v => v._id === this.selectedVersionId) || null;
    }
  },
  methods: {
    updateNewDep(newDep) {
      this.$emit("update:newDep", newDep);
    },
    
    async loadAssets() {
      this.loading = true;
      try {
        const response = await assetService.getAssets({ take: 200, view: 'latest' });
        this.assets = response.assets || [];
      } catch (err) {
        console.error('Error loading assets:', err);
        this.$notify({
          type: "error",
          text: "Failed to load assets"
        });
      }
      this.loading = false;
    },
    
    selectAsset(asset) {
      this.selectedAsset = asset;
      this.assetVersions = [];
      this.selectedVersionId = null;
      this.loadAssetVersions(asset._id);
    },

    async loadAssetVersions(assetId) {
      this.versionsLoading = true;
      try {
        const response = await assetService.getAssetVersions(assetId);
        this.assetVersions = (response.versions || []).map(version => ({
          ...version,
          version: version.version || 1
        }));
        if (response.versionModel && this.selectedAsset) {
          this.selectedAsset.version_meta = response.versionModel;
        }
        const defaultVersionNumber = response.versionModel?.published_version;
        if (defaultVersionNumber) {
          const defaultVersion = this.assetVersions.find(v => v.version === defaultVersionNumber);
          this.selectedVersionId = defaultVersion?._id || this.assetVersions[0]?._id || null;
        } else {
          this.selectedVersionId = this.assetVersions[0]?._id || null;
        }
      } catch (err) {
        console.error('Error loading versions:', err);
        this.$notify({
          type: "error",
          text: "Failed to load versions"
        });
      }
      this.versionsLoading = false;
    },
    
    confirmSelection() {
      if (!this.selectedAsset) {
        this.$notify({ type: "warn", text: "Please select an asset" });
        return;
      }
      if (!this.selectedVersion) {
        this.$notify({ type: "warn", text: "Please select a version" });
        return;
      }
      const detectedType = this.detectFileType(this.selectedVersion.file_type, this.selectedVersion.original_name);
      this.$emit("update:newDep", {
        ...this.newDep,
        type: detectedType,
        url: `file:${this.selectedVersion.s3_key}`,
        is_global: true,
        asset_id: this.selectedVersion._id,
        asset_version: this.selectedVersion.version || 1,
        asset_name: this.selectedAsset.name,
        asset_original_name: this.selectedVersion.original_name,
        asset_version_note: this.selectedVersion.version_note || ''
      });
      this.$emit("addDepFile");
      this.showAssetModal = false;
      this.selectedAsset = null;
      this.assetVersions = [];
      this.selectedVersionId = null;
    },
    
    detectFileType(fileType, originalName) {
      if (fileType.startsWith('image/')) return 'image';
      if (fileType.startsWith('video/')) return 'video';
      if (fileType === 'application/javascript' || 
          fileType === 'text/javascript' || 
          fileType === 'application/x-javascript' ||
          (fileType.startsWith('text/') && originalName.match(/\.(js|jsx|ts|tsx)$/i))) {
        return 'script';
      }
      if (fileType === 'text/css' || 
          (fileType.startsWith('text/') && originalName.match(/\.(css|scss|sass|less)$/i))) {
        return 'style';
      }
      // Default to script for other text files
      if (fileType.startsWith('text/') || fileType === 'application/json') return 'script';
      return 'script'; // Default fallback
    },
    
    async handleUploadFile(ev) {
      this.$emit("update:newDep", { ...this.newDep, loading: true });
      try {
        const res = await uploadFile(ev.target.files[0]);
        const detectedType = this.detectFileType(ev.target.files[0].type || '', ev.target.files[0].name || '');
        this.$emit("update:newDep", {
          ...this.newDep,
          url: `file:${res.key}`,
          type: detectedType,
          loading: false
        });
      } catch (err) {
        console.error('Upload error:', err);
        this.$notify({
          type: "error",
          text: "Failed to upload file"
        });
        this.$emit("update:newDep", { ...this.newDep, loading: false });
      }
    },
    
    addDepFile() {
      this.$emit("addDepFile");
    },
    
    getFileIcon(fileType, originalName = '') {
      if (fileType.startsWith('image/')) return 'fa-solid fa-image text-green-600';
      if (fileType.startsWith('video/')) return 'fa-solid fa-video text-blue-600';
      if (fileType === 'application/javascript' || 
          fileType === 'text/javascript' || 
          fileType === 'application/x-javascript' ||
          (fileType.startsWith('text/') && originalName.match(/\.(js|jsx|ts|tsx)$/i))) {
        return 'fa-solid fa-file-code text-orange-600';
      }
      if (fileType === 'text/css' || 
          (fileType.startsWith('text/') && originalName.match(/\.(css|scss|sass|less)$/i))) {
        return 'fa-solid fa-palette text-purple-600';
      }
      if (fileType.startsWith('text/') || fileType === 'application/json') return 'fa-solid fa-file-code text-orange-600';
      return 'fa-solid fa-file text-gray-600';
    },
    
    getFileTypeLabel(fileType, originalName = '') {
      if (fileType.startsWith('image/')) return 'Image';
      if (fileType.startsWith('video/')) return 'Video';
      if (fileType === 'application/javascript' || 
          fileType === 'text/javascript' || 
          fileType === 'application/x-javascript' ||
          (fileType.startsWith('text/') && originalName.match(/\.(js|jsx|ts|tsx)$/i))) {
        return 'Script';
      }
      if (fileType === 'text/css' || 
          (fileType.startsWith('text/') && originalName.match(/\.(css|scss|sass|less)$/i))) {
        return 'Style';
      }
      if (fileType.startsWith('text/') || fileType === 'application/json') return 'Code';
      return 'File';
    },
    
    getTypeBadgeClass(fileType, originalName = '') {
      if (fileType.startsWith('image/')) return 'bg-green-100 text-green-800';
      if (fileType.startsWith('video/')) return 'bg-blue-100 text-blue-800';
      if (fileType === 'application/javascript' || 
          fileType === 'text/javascript' || 
          fileType === 'application/x-javascript' ||
          (fileType.startsWith('text/') && originalName.match(/\.(js|jsx|ts|tsx)$/i))) {
        return 'bg-orange-100 text-orange-800';
      }
      if (fileType === 'text/css' || 
          (fileType.startsWith('text/') && originalName.match(/\.(css|scss|sass|less)$/i))) {
        return 'bg-purple-100 text-purple-800';
      }
      if (fileType.startsWith('text/') || fileType === 'application/json') return 'bg-orange-100 text-orange-800';
      return 'bg-gray-100 text-gray-800';
    },
    
    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString();
    }
  },
  watch: {
    showAssetModal(newVal) {
      if (newVal) {
        this.loadAssets();
      } else {
        this.selectedAsset = null;
        this.assetVersions = [];
        this.selectedVersionId = null;
      }
    }
  }
};
</script>

<style scoped>
.custom-file-input {
  background: #00aeef;
}

.custom-file-input:hover {
  background: #02a2dd;
}

.fa-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
